import { useEffect, useRef, useState } from "react";
import { LayersControl, MapContainer, TileLayer, useMap } from "react-leaflet";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";

interface ControlPointMapEditorProps {
  geometry: GeoJSON.Geometry | null;
  onGeometryChange?: (geometry: GeoJSON.Geometry | null) => void;
  editable?: boolean;
  referencePlots?: GeoJSON.FeatureCollection | null;
}

/**
 * Component to add drawing controls to the map (Point and Polygon)
 */
const DrawControls = ({
  onGeometryChange,
  geometry,
  layerRef,
}: {
  onGeometryChange?: (geometry: GeoJSON.Geometry | null) => void;
  geometry: GeoJSON.Geometry | null;
  layerRef?: React.MutableRefObject<L.FeatureGroup | null>;
}) => {
  const map = useMap();
  const { t } = useTranslation();
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());

  useEffect(() => {
    const drawnItems = drawnItemsRef.current;
    
    // Expose layer to parent component
    if (layerRef) {
      layerRef.current = drawnItems;
    }
    
    map.addLayer(drawnItems);

    // Initialize draw control with point and polygon
    const drawControl = new L.Control.Draw({
      position: "topleft",
      edit: {
        featureGroup: drawnItems,
      },
      draw: {
        polygon: {
          allowIntersection: false,
          // Desactivado a propósito. Con `showArea: true` leaflet-draw
          // llama a `L.GeometryUtil.readableArea` tras cada vértice
          // añadido, y ese util no existe en Leaflet ≥ 1.8 → la
          // excepción rompe el flujo de dibujo y el usuario sólo
          // puede añadir 2-3 vértices antes de que se congele. El
          // `PlotMapEditor` ya aplica el mismo workaround.
          showArea: false,
          metric: true,
          shapeOptions: {
            color: "#ff4d4f",
            weight: 3,
            fillOpacity: 0.3,
            fillColor: "#ff4d4f",
          },
          repeatMode: false,
        },
        marker: {
          icon: new L.Icon({
            iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
            iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
            shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
          }),
        },
        polyline: false,
        rectangle: false,
        circle: false,
        circlemarker: false,
      },
    });

    map.addControl(drawControl);

    // Handle draw created event
    map.on(L.Draw.Event.CREATED, (event: any) => {
      const layer = event.layer;
      const layerType = event.layerType;
      
      // Clear existing layers (only one geometry at a time)
      drawnItems.clearLayers();
      drawnItems.addLayer(layer);
      
      const geoJson = layer.toGeoJSON();
      const newGeometry = geoJson.geometry;
      
      if (onGeometryChange) {
        onGeometryChange(newGeometry);
      }
      
      if (layerType === 'marker') {
        message.success(t("map.point_created") || "Punto creado correctamente");
      } else if (layerType === 'polygon') {
        message.success(t("map.polygon_created") || "Polígono creado correctamente");
      }
    });

    // Handle draw edited event
    map.on(L.Draw.Event.EDITED, () => {
      const layers = drawnItems.getLayers();
      
      if (layers.length > 0) {
        const geoJson = (layers[0] as any).toGeoJSON();
        const newGeometry = geoJson.geometry;
        
        if (onGeometryChange) {
          onGeometryChange(newGeometry);
        }
        
        message.success(t("map.geometry_edited") || "Geometría editada correctamente");
      }
    });

    // Handle draw deleted event
    map.on(L.Draw.Event.DELETED, () => {
      if (onGeometryChange) {
        onGeometryChange(null);
      }
      
      message.success(t("map.geometry_deleted") || "Geometría eliminada");
    });

    // Cleanup
    return () => {
      map.removeControl(drawControl);
      map.removeLayer(drawnItems);
      map.off(L.Draw.Event.CREATED);
      map.off(L.Draw.Event.EDITED);
      map.off(L.Draw.Event.DELETED);
    };
  }, [map, onGeometryChange, t]);

  return (
    <>
      <GeometryDisplay geometry={geometry} drawnItems={drawnItemsRef.current} />
    </>
  );
};

/**
 * Component to display reference plots from the farm
 */
const ReferencePlotsLayer = ({ 
  referencePlots,
  layerRef
}: { 
  referencePlots: GeoJSON.FeatureCollection | null;
  layerRef?: React.MutableRefObject<L.FeatureGroup | null>;
}) => {
  const map = useMap();
  const layerGroupRef = useRef<L.FeatureGroup>(new L.FeatureGroup());

  useEffect(() => {
    const layerGroup = layerGroupRef.current;
    
    // Expose layer to parent component
    if (layerRef) {
      layerRef.current = layerGroup;
    }
    
    map.addLayer(layerGroup);

    return () => {
      map.removeLayer(layerGroup);
    };
  }, [map, layerRef]);

  useEffect(() => {
    const layerGroup = layerGroupRef.current;
    layerGroup.clearLayers();

    if (!referencePlots || !referencePlots.features) return;

    referencePlots.features.forEach((feature) => {
      if (!feature.geometry) return;

      const geometry = feature.geometry;
      let layer: L.Layer | null = null;

      if (geometry.type === 'Polygon') {
        const coords = (geometry as any).coordinates[0];
        layer = L.polygon(
          coords.map((coord: number[]) => [coord[1], coord[0]]),
          {
            color: '#1890ff',
            weight: 2,
            fillOpacity: 0.1,
            fillColor: '#1890ff',
          }
        );
      } else if (geometry.type === 'MultiPolygon') {
        const coords = (geometry as any).coordinates;
        const polygons = coords.map((polygonCoords: any) =>
          polygonCoords[0].map((coord: number[]) => [coord[1], coord[0]])
        );
        layer = L.polygon(polygons, {
          color: '#1890ff',
          weight: 2,
          fillOpacity: 0.1,
          fillColor: '#1890ff',
        });
      }

      if (layer) {
        // Add tooltip with plot info
        const props = feature.properties || {};
        const tooltipContent = `
          <div style="font-size: 12px;">
            <strong>Parcela Auditada</strong><br/>
            ${props.name ? `Nombre: ${props.name}<br/>` : ''}
            ${props.surface ? `Superficie: ${props.surface.toFixed(2)} ha` : ''}
          </div>
        `;
        layer.bindTooltip(tooltipContent);
        layerGroup.addLayer(layer);
      }
    });

    // Fit map bounds to reference plots after adding them
    if (layerGroup.getLayers().length > 0) {
      const bounds = layerGroup.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
      }
    }
  }, [referencePlots, map]);

  return null;
};

/**
 * Component to display existing geometry and load it into drawnItems for editing
 */
const GeometryDisplay = ({ 
  geometry,
  drawnItems 
}: { 
  geometry: GeoJSON.Geometry | null;
  drawnItems: L.FeatureGroup;
}) => {
  const map = useMap();

  useEffect(() => {
    if (!geometry) return;

    // Clear existing items
    drawnItems.clearLayers();

    let layer: L.Layer | null = null;

    if (geometry.type === "Point") {
      const coords = (geometry as any).coordinates;
      layer = L.marker([coords[1], coords[0]], {
        icon: new L.Icon({
          iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
          shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        }),
      });
    } else if (geometry.type === "Polygon") {
      const coords = (geometry as any).coordinates[0];
      layer = L.polygon(
        coords.map((coord: number[]) => [coord[1], coord[0]]),
        {
          color: "#ff4d4f",
          weight: 3,
          fillOpacity: 0.3,
          fillColor: "#ff4d4f",
        }
      );
    } else if (geometry.type === "MultiPolygon") {
      const coords = (geometry as any).coordinates;
      const polygons = coords.map((polygonCoords: any) =>
        polygonCoords[0].map((coord: number[]) => [coord[1], coord[0]])
      );
      layer = L.polygon(polygons, {
        color: "#ff4d4f",
        weight: 3,
        fillOpacity: 0.3,
        fillColor: "#ff4d4f",
      });
    }

    if (layer) {
      drawnItems.addLayer(layer);

      // Fit bounds to geometry
      const bounds = drawnItems.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
      }
    }

    return () => {
      // Don't remove layers here as they need to stay for editing
    };
  }, [map, geometry, drawnItems]);

  return null;
};

/**
 * Component to add coordinate search control
 */
const CoordinateSearchControl = () => {
  const map = useMap();
  const { t } = useTranslation();

  useEffect(() => {
    const CoordControl = L.Control.extend({
      options: {
        position: "topright",
      },

      onAdd: function (map: L.Map) {
        const container = L.DomUtil.create(
          "div",
          "leaflet-bar leaflet-control leaflet-control-custom"
        );
        container.style.backgroundColor = "white";
        container.style.padding = "8px";
        container.style.borderRadius = "4px";
        container.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";

        const input = L.DomUtil.create("input", "", container);
        input.type = "text";
        input.placeholder = t("map.search_input_placeholder") || "Ciudad o Lat, Lng";
        input.style.width = "220px";
        input.style.padding = "4px 8px";
        input.style.border = "1px solid #d9d9d9";
        input.style.borderRadius = "2px";
        input.style.marginRight = "4px";

        const button = L.DomUtil.create("button", "", container);
        button.innerHTML = t("map.search_button") || "🔍 Buscar";
        button.style.padding = "4px 12px";
        button.style.backgroundColor = "#1890ff";
        button.style.color = "white";
        button.style.border = "none";
        button.style.borderRadius = "2px";
        button.style.cursor = "pointer";

        const handleCenter = async () => {
          const value = input.value.trim();

          if (!value) {
            message.warning(t("map.search_no_input") || "Por favor ingrese una ubicación o coordenadas");
            return;
          }

          const parts = value.split(",").map((p) => p.trim());

          if (parts.length === 2) {
            const lat = parseFloat(parts[0]);
            const lng = parseFloat(parts[1]);

            if (!isNaN(lat) && !isNaN(lng)) {
              if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                message.error(t("map.search_invalid_coords") || "Coordenadas fuera de rango válido");
                return;
              }

              map.setView([lat, lng], 16);
              message.success(t("map.search_centered") || "Mapa centrado en las coordenadas");
              return;
            }
          }

          button.disabled = true;
          button.innerHTML = t("map.search_button_loading") || "⏳ Buscando...";

          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
                value
              )}&countrycodes=es&limit=1`
            );

            if (!response.ok) {
              throw new Error("Error en la búsqueda");
            }

            const data = await response.json();

            if (data.length === 0) {
              message.error(
                t("map.search_not_found") || "No se encontró la ubicación"
              );
              return;
            }

            const result = data[0];
            const lat = parseFloat(result.lat);
            const lng = parseFloat(result.lon);

            map.setView([lat, lng], 14);
            message.success(`${t("map.search_location_found") || "Ubicación encontrada"}: ${result.display_name}`);
          } catch (error) {
            message.error(t("map.search_error") || "Error al buscar la ubicación");
            console.error("Geocoding error:", error);
          } finally {
            button.disabled = false;
            button.innerHTML = t("map.search_button") || "🔍 Buscar";
          }
        };

        button.onclick = handleCenter;
        input.onkeypress = (e) => {
          if (e.key === "Enter") {
            handleCenter();
          }
        };

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        return container;
      },
    });

    const control = new CoordControl();
    map.addControl(control);

    return () => {
      map.removeControl(control);
    };
  }, [map, t]);

  return null;
};

/**
 * Component to add layer control
 */
const LayerToggleControl = ({
  referencePlotsLayer,
  controlPointLayer,
}: {
  referencePlotsLayer: L.FeatureGroup | null;
  controlPointLayer: L.FeatureGroup | null;
}) => {
  const map = useMap();
  const controlRef = useRef<L.Control | null>(null);

  useEffect(() => {
    if (!referencePlotsLayer && !controlPointLayer) return;

    const LayerControl = L.Control.extend({
      options: {
        position: "topright",
      },

      onAdd: function (mapInstance: L.Map) {
        const container = L.DomUtil.create(
          "div",
          "leaflet-bar leaflet-control leaflet-control-layers"
        );
        container.style.backgroundColor = "white";
        container.style.padding = "10px";
        container.style.borderRadius = "4px";
        container.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
        container.style.minWidth = "180px";

        const title = L.DomUtil.create("div", "", container);
        title.innerHTML = "<strong>Capas</strong>";
        title.style.marginBottom = "8px";
        title.style.fontSize = "14px";

        if (referencePlotsLayer) {
          const refDiv = L.DomUtil.create("div", "", container);
          refDiv.style.marginBottom = "6px";
          refDiv.style.display = "flex";
          refDiv.style.alignItems = "center";

          const refCheckbox = L.DomUtil.create("input", "", refDiv) as HTMLInputElement;
          refCheckbox.type = "checkbox";
          refCheckbox.checked = true;
          refCheckbox.style.marginRight = "8px";
          refCheckbox.style.cursor = "pointer";
          refCheckbox.id = "ref-plots-checkbox";

          const refLabel = L.DomUtil.create("label", "", refDiv);
          refLabel.innerHTML = "Parcelas Auditadas";
          refLabel.style.cursor = "pointer";
          refLabel.style.fontSize = "13px";
          refLabel.htmlFor = "ref-plots-checkbox";

          L.DomEvent.on(refCheckbox, 'change', function(e: Event) {
            L.DomEvent.stopPropagation(e);
            const checkbox = e.target as HTMLInputElement;
            
            if (checkbox.checked) {
              if (!mapInstance.hasLayer(referencePlotsLayer)) {
                mapInstance.addLayer(referencePlotsLayer);
              }
            } else {
              if (mapInstance.hasLayer(referencePlotsLayer)) {
                mapInstance.removeLayer(referencePlotsLayer);
              }
            }
          });
        }

        if (controlPointLayer) {
          const cpDiv = L.DomUtil.create("div", "", container);
          cpDiv.style.display = "flex";
          cpDiv.style.alignItems = "center";

          const cpCheckbox = L.DomUtil.create("input", "", cpDiv) as HTMLInputElement;
          cpCheckbox.type = "checkbox";
          cpCheckbox.checked = true;
          cpCheckbox.style.marginRight = "8px";
          cpCheckbox.style.cursor = "pointer";
          cpCheckbox.id = "cp-checkbox";

          const cpLabel = L.DomUtil.create("label", "", cpDiv);
          cpLabel.innerHTML = "Punto de Control";
          cpLabel.style.cursor = "pointer";
          cpLabel.style.fontSize = "13px";
          cpLabel.htmlFor = "cp-checkbox";

          L.DomEvent.on(cpCheckbox, 'change', function(e: Event) {
            L.DomEvent.stopPropagation(e);
            const checkbox = e.target as HTMLInputElement;
            
            if (checkbox.checked) {
              if (!mapInstance.hasLayer(controlPointLayer)) {
                mapInstance.addLayer(controlPointLayer);
              }
            } else {
              if (mapInstance.hasLayer(controlPointLayer)) {
                mapInstance.removeLayer(controlPointLayer);
              }
            }
          });
        }

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        return container;
      },
    });

    const control = new LayerControl();
    controlRef.current = control;
    map.addControl(control);

    return () => {
      if (controlRef.current) {
        map.removeControl(controlRef.current);
        controlRef.current = null;
      }
    };
  }, [map, referencePlotsLayer, controlPointLayer]);

  return null;
};

/**
 * ControlPointMapEditor Component
 * Map component for viewing and editing control point geometry (Point or Polygon)
 */
export const ControlPointMapEditor = ({ 
  geometry, 
  onGeometryChange,
  editable = true,
  referencePlots = null
}: ControlPointMapEditorProps) => {
  const referencePlotsLayerRef = useRef<L.FeatureGroup | null>(null);
  const controlPointLayerRef = useRef<L.FeatureGroup | null>(null);

  const getCenter = (): [number, number] => {
    if (!geometry) {
      // Try to get center from reference plots
      if (referencePlots && referencePlots.features && referencePlots.features.length > 0) {
        const firstFeature = referencePlots.features[0];
        if (firstFeature.geometry) {
          const geom = firstFeature.geometry;
          if (geom.type === 'Polygon') {
            const coords = (geom as any).coordinates[0][0];
            return [coords[1], coords[0]];
          } else if (geom.type === 'MultiPolygon') {
            const coords = (geom as any).coordinates[0][0][0];
            return [coords[1], coords[0]];
          }
        }
      }
      return [40.0, -4.0]; // Default center (Spain)
    }

    if (geometry.type === "Point") {
      const coords = (geometry as any).coordinates;
      return [coords[1], coords[0]];
    }

    const coords: any = (geometry as any).coordinates;
    
    if (!coords) {
      return [40.0, -4.0];
    }

    let coordArray: any = coords;
    
    if (geometry.type === "Polygon" && Array.isArray(coords) && coords.length > 0) {
      coordArray = coords[0];
    } else if (geometry.type === "MultiPolygon" && Array.isArray(coords) && coords.length > 0) {
      coordArray = coords[0][0];
    }

    if (Array.isArray(coordArray) && coordArray.length > 0) {
      let sumLat = 0;
      let sumLng = 0;
      let count = 0;

      coordArray.forEach((coord: any) => {
        if (Array.isArray(coord) && coord.length >= 2) {
          sumLng += coord[0];
          sumLat += coord[1];
          count++;
        }
      });

      if (count > 0) {
        return [sumLat / count, sumLng / count];
      }
    }

    return [40.0, -4.0];
  };

  return (
    <div
      className="control-point-map-editor"
      style={{ position: "relative", height: "100%", width: "100%" }}
    >
      <style>{`
        /* Search box and base-layer picker share the same row in the
           top-right corner — same layout as the plot map editor. */
        .control-point-map-editor .leaflet-top.leaflet-right {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          gap: 6px;
        }
        .control-point-map-editor .leaflet-top.leaflet-right > .leaflet-control {
          margin: 10px 0 0 0;
          float: none;
          clear: none;
        }
        .control-point-map-editor .leaflet-top.leaflet-right > .leaflet-control:last-child {
          margin-right: 10px;
        }
      `}</style>
      <MapContainer
        center={getCenter()}
        zoom={geometry ? 15 : (referencePlots ? 14 : 6)}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={true}
      >
        {/* Coordinate search — rendered first so it sits to the
            left of the base-layer picker on the same row. */}
        <CoordinateSearchControl />

        {/* Base layers — same picker as the plot map / audit detail. */}
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Satélite (ArcGIS)">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution="&copy; Esri"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satélite (Google)">
            <TileLayer
              url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
              attribution="&copy; Google"
              maxZoom={20}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="OpenStreetMap">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="IGN Base">
            <TileLayer
              url="https://tms-ign-base.idee.es/1.0.0/IGNBaseTodo/{z}/{x}/{-y}.jpeg"
              attribution="&copy; IGN"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        {referencePlots && <ReferencePlotsLayer referencePlots={referencePlots} layerRef={referencePlotsLayerRef} />}

        {editable && <DrawControls onGeometryChange={onGeometryChange} geometry={geometry} layerRef={controlPointLayerRef} />}
      </MapContainer>
    </div>
  );
};
